import { useState } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { AuthGate } from '@nsi-advisory-services/auth-react';

/* Pages */
import HomePage from './pages/HomePage';
import ConfigurationPage from './pages/ConfigurationPage';
import ConfigurationHomePage from './pages/ConfigurationHomePage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskActualsPage from './pages/TaskActualsPage';
import ProjectActualsPage from './pages/ProjectActualsPage';
import UserDetailPage from './pages/UserDetailPage';
import ActivityHourForecastPage from './pages/ActivityHourForecastPage';
import TaskOdcForecastPage from './pages/TaskOdcForecastPage';
import ChartsPage from './pages/ChartsPage';
import MyWorkplanPage from './pages/MyWorkplanPage';
import ProjectCreateWizardPage from './pages/ProjectCreateWizardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectGanttPage from './pages/ProjectGanttPage';
import TaskOrderDetailPage from './pages/TaskOrderDetailPage';
import TaskOrderInvoicesPage from './pages/TaskOrderInvoicesPage';
import StaffingOutlookPage from './pages/StaffingOutlookPage';
import StaffingScenariosPage from './pages/StaffingScenariosPage';
import WbsManagerPage from './pages/WbsManagerPage';
import EmployeeConnectCloseDatesSamplePage from './pages/EmployeeConnectCloseDatesSamplePage';
import ImportHoursPage from './pages/ImportHoursPage';
import TimeOffManagerPage from './pages/TimeOffManagerPage';
import HelpPage from './pages/HelpPage';
import HelpArticlePage from './pages/HelpArticlePage';

/* Components */
import Sidebar from './components/navigation/Sidebar';
import BottomNav from './components/navigation/BottomNav';
import RequireAccess from './components/RequireAccess';
import ImpersonationBar from './components/ImpersonationBar';
import { canCreateProjects, canOpenUserManagement } from './hooks/usePermissions';

import './App.css';

// Route gates; the server makes the same checks.
const needs = (key) => (p) => p.can(key);
const canViewProject = (p, { projectId }) => p.canProject(projectId, 'view');
const canStaffProject = (p, { projectId }) => p.canProject(projectId, 'finance');

const gate = (allowed, element) => <RequireAccess allowed={allowed}>{element}</RequireAccess>;

function AppShell() {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

    return (
        <AuthGate appName="ProPlan Connect">
            <div
                className="app-shell"
                data-sidebar-collapsed={sidebarCollapsed ? 'true' : 'false'}
            >
                <Sidebar
                    collapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed((v) => !v)}
                />

                <div className="main-content">
                    <ImpersonationBar />
                    <div className="p-3">
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/projects/new" element={gate(canCreateProjects, <ProjectCreateWizardPage />)} />
                            <Route path="/projects/:projectId" element={gate(canViewProject, <ProjectDetailPage />)} />
                            <Route path="/projects/:projectId/gantt" element={gate(canViewProject, <ProjectGanttPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId" element={gate(canViewProject, <TaskOrderDetailPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/gantt" element={gate(canViewProject, <ProjectGanttPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/invoices" element={gate(canViewProject, <TaskOrderInvoicesPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/staffing-outlook" element={gate(canStaffProject, <StaffingOutlookPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/staffing-outlook/scenarios" element={gate(canStaffProject, <StaffingScenariosPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId" element={gate(canViewProject, <TaskDetailPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/actuals" element={gate(canViewProject, <TaskActualsPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/actuals" element={gate(canViewProject, <ProjectActualsPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/activities/:activityId/forecast" element={gate(canViewProject, <ActivityHourForecastPage />)} />
                            <Route path="/projects/:projectId/task-orders/:taskOrderId/tasks/:taskId/odc-forecast" element={gate(canViewProject, <TaskOdcForecastPage />)} />
                            <Route path="/projects/:projectId/actuals" element={gate(canViewProject, <ProjectActualsPage />)} />
                            <Route path="/projects/:projectId/import-hours" element={gate(canViewProject, <ImportHoursPage />)} />
                            <Route path="/my-workplan" element={<MyWorkplanPage />} />
                            <Route path="/charts" element={gate(needs('charts.view'), <ChartsPage />)} />
                            <Route path="/wbs-manager" element={gate(needs('wbs.view'), <WbsManagerPage />)} />
                            <Route path="/import-hours" element={<ImportHoursPage />} />
                            <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                            <Route path="/tasks/:taskId/actuals" element={<TaskActualsPage />} />
                            <Route path="/tasks/:taskId/activities/:activityId/forecast" element={<ActivityHourForecastPage />} />
                            <Route path="/tasks/:taskId/odc-forecast" element={<TaskOdcForecastPage />} />
                            <Route path="/configuration" element={<ConfigurationHomePage />} />
                            <Route path="/configuration/integrations" element={gate(needs('integrations.manage'), <ConfigurationPage mode="integrations" />)} />
                            <Route path="/configuration/users" element={gate(canOpenUserManagement, <ConfigurationPage mode="users" />)} />
                            <Route path="/configuration/projects" element={gate(canCreateProjects, <ConfigurationPage mode="projects" />)} />
                            <Route path="/configuration/time-off" element={<TimeOffManagerPage />} />
                            <Route path="/connect-samples/employeeconnect-close-dates" element={<EmployeeConnectCloseDatesSamplePage />} />
                            <Route path="/users/:userId" element={gate(needs('rates.manage'), <UserDetailPage />)} />
                            <Route path="/help" element={<HelpPage />} />
                            <Route path="/help/:articleId" element={<HelpArticlePage />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </div>
                </div>
            </div>

            <BottomNav />
        </AuthGate>
    );
}

// A data router (rather than <BrowserRouter>) so pages can use useBlocker to hold navigation while
// they have unsaved changes. The whole shell, with its own <Routes>, renders under one catch-all
// route, so the route table above is unchanged.
const router = createBrowserRouter([{ path: '*', element: <AppShell /> }]);

function App() {
    return (
        <div className="App">
            <RouterProvider router={router} />
        </div>
    );
}

export default App;
